import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useGetTableData2, useCreateData } from "../Plan/tableHooks";
import { AG_GRID_LOCALE_KR } from "@ag-grid-community/locale";
import { Snackbar, Alert, TextField, InputAdornment } from "@mui/material";
import { ButtonBox, GridContainer } from "./styles";
import axios from "axios";
import APS_URL from "../../../utils/APS_URL";
import Loading from "../Loading/Loading";
import {
  isCompletedQtyChanged,
  getCompletedQtyChangedRows,
  getRightRowStyle,
} from "./util";
import { rightColumn, noRowsOverlayComponent, leftColumn } from "./constants";
import BulkSelectionButton from "./BulkSelectionButton";
import SearchIcon from "@mui/icons-material/Search";
import {
  SaveButton as CustomSaveButton,
  CustomButton as CustomMuiButton,
} from "../../common/Buttons";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import { useParams } from "react-router-dom";

ModuleRegistry.registerModules([AllCommunityModule]);

function PerformanceGrid() {
  const { group_code, project_code } = useParams();
  const [leftGridData, setLeftGridData] = useState([]);
  const [rightGridData, setRightGridData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rightSelectedRows, setRightSelectedRows] = useState([]);
  const [rightOriginalData, setRightOriginalData] = useState([]);
  const [changeCounter, setChangeCounter] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leftColDef] = useState(leftColumn);
  const [rightColDef] = useState(rightColumn);
  const [leftSearchText, setLeftSearchText] = useState("");
  const [rightSearchText, setRightSearchText] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });
  const leftGridRef = useRef();
  const rightGridRef = useRef();
  const { currentProgressData } = useGetTableData2(
    refreshStatus,
    `/api/performance/getProgress?group_code=${group_code}&project_code=${project_code}`,
    "currentProgressData"
  );
  useEffect(() => {
    setLeftGridData(currentProgressData);
  }, [currentProgressData]);
  const onLeftFilterChanged = useCallback((event) => {
    setLeftSearchText(event.target.value);
  }, []);
  const onRightFilterChanged = useCallback((event) => {
    setRightSearchText(event.target.value);
  }, []);
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  const createMutation = useCreateData(
    null,
    // 원래 postInsightwinPerformanceUpdate가 맞지만
    // Ai Test 를 위하여 postInsightwinPerformanceUpdateForAiTest 를 입력함.
    "/api/performance/updatePerformance",
    openSnackbar
  );
  const customGermanLocale = {
    ...AG_GRID_LOCALE_KR,
  };
  const onLeftGridClick = async (e) => {
    const rowData = e.data;
    const rowNode = leftGridRef.current.api.getRowNode(e.node.id);
    const isRowSelected = rowNode.isSelected();
    leftGridRef.current.api.forEachNode((node) => {
      if (node.id !== e.node.id) {
        node.setSelected(false);
        node.setDataValue("style_field", false);
      }
    });
    if (!isRowSelected) {
      rowNode.setSelected(true);
      rowNode.setDataValue("style_field", true);
      setSelectedRows([rowData]);
    } else {
      rowNode.setSelected(false);
      rowNode.setDataValue("style_field", false);
      setSelectedRows([]);
    }
    try {
      if (!isRowSelected) {
        if (
          !rightGridData
            .map((content) => content.order_id)
            .some((c) => c === e.data.order_id)
        ) {
          const res = await axios.get(
            APS_URL +
              `/api/performance/getInsertPerformance?order_id=${e.data.order_id}&group_code=${group_code}&project_code=${project_code}`
          );
          if (!res.data.length) {
            openSnackbar("해당 아이템에 등록된 공정이 없습니다.", "info");
            setRightGridData([]);
            setRightOriginalData([]);
            return;
          }
          setRightGridData(res.data);
          const deepCopiedArray = res.data.map((item) =>
            JSON.parse(JSON.stringify(item))
          );
          setRightOriginalData(deepCopiedArray);
        }
      } else {
        const removeRightGridData = rightGridData.filter(
          (c) => c.order_id !== e.data.order_id
        );
        setRightGridData(removeRightGridData);
        const deepCopiedArray = removeRightGridData.map((item) =>
          JSON.parse(JSON.stringify(item))
        );
        setRightOriginalData(deepCopiedArray);
      }
    } catch (e) {
      console.log(e);
    }
  };
  const onRightSelectionChanged = () => {
    const selectedNodes = rightGridRef.current.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    setRightSelectedRows(selectedData);
  };
  const rowLeftSelection = useMemo(() => {
    return {
      mode: "singleRow",
    };
  }, []);
  const rowRightSelection = useMemo(() => {
    return {
      mode: "multiRow",
    };
  }, []);
  const onChangeRightQty = (e) => {
    const rowNode = rightGridRef.current.api.getRowNode(e.node.id);
    if (e.colDef.field === "completed_qty") {
      setChangeCounter((prev) => prev + 1);
    }
    if (e.data.qty < Number(e.value)) {
      openSnackbar("전체 수량보다 높게 입력할 수 없습니다", "warning");
      rowNode.setDataValue(e.column.colId, e.data.qty);
      const compareToOriginalData = (() => {
        const originalValue = rightOriginalData.filter(
          (c) => c.order_id === e.data.order_id && c.proc_id === e.data.proc_id
        )[0]?.completed_qty;
        return (
          (originalValue === null || originalValue === undefined
            ? 0
            : Number(originalValue)) ===
          (e.data.qty === null || e.data.qty === undefined
            ? 0
            : Number(e.data.qty))
        );
      })();
      rowNode.setDataValue("style_field", !compareToOriginalData);
      return;
    }
    rowNode.setDataValue(e.column.colId, e.value);
    const compareToOriginalData = (() => {
      const originalValue = rightOriginalData.filter(
        (c) => c.order_id === e.data.order_id && c.proc_id === e.data.proc_id
      )[0]?.completed_qty;
      return (
        (originalValue === null || originalValue === undefined
          ? 0
          : Number(originalValue)) ===
        (e.value === null || e.value === undefined ? 0 : Number(e.value))
      );
    })();
    rowNode.setDataValue("style_field", !compareToOriginalData);
    return;
  };
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      resizable: true,
      suppressAutoSize: false,
      wrapText: false,
      filter: true,
    };
  }, []);
  const handleRightComplete = () => {
    if (!rightSelectedRows || rightSelectedRows.length === 0) {
      openSnackbar("선택된 행이 없습니다.", "warning");
      return;
    }
    if (!rightGridRef.current || !rightGridRef.current.api) {
      console.error("Grid API is not available");
      return;
    }
    const selectedIdentifiers = rightSelectedRows.map(
      (row) => `${row.order_id}_${row.proc_id}`
    );
    setRightGridData((prevRightGridData) => {
      return prevRightGridData.map((row) => {
        const rowIdentifier = `${row.order_id}_${row.proc_id}`;
        if (selectedIdentifiers.includes(rowIdentifier)) {
          try {
            let rowNode;
            if (row.id) {
              rowNode = rightGridRef.current.api.getRowNode(row.id);
            }
            if (!rowNode) {
              const rowIndex = prevRightGridData.findIndex(
                (r) => r.order_id === row.order_id && r.proc_id === row.proc_id
              );
              if (rowIndex >= 0) {
                rowNode =
                  rightGridRef.current.api.getDisplayedRowAtIndex(rowIndex);
              }
            }
            if (!rowNode) {
              const possibleKeys = [
                `${row.order_id}_${row.proc_id}`,
                `${row.order_id}_${row.item_id}_${row.proc_id}`,
                row.order_id.toString(),
                row.proc_id.toString(),
              ];
              for (const key of possibleKeys) {
                const node = rightGridRef.current.api.getRowNode(key);
                if (node) {
                  rowNode = node;
                  break;
                }
              }
            }
            if (rowNode) {
              rowNode.setDataValue("style_field", true);
            } else {
              console.warn(
                `행 노드를 찾을 수 없음: order_id=${row.order_id}, proc_id=${row.proc_id}`
              );
            }
          } catch (error) {
            console.error("행 노드 업데이트 중 오류 발생:", error);
          }
          return {
            ...row,
            completed_qty: row.qty,
            style_field: true,
          };
        }
        return row;
      });
    });
    openSnackbar("선택한 항목의 완료 수량이 업데이트되었습니다.", "success");
  };
  const SaveButton = () => {
    const hasChanges = useMemo(
      () => isCompletedQtyChanged(rightGridData, rightOriginalData),
      [rightGridData, rightOriginalData, changeCounter]
    );
    const handleSaveClick = useCallback(() => {
      const changedRows = getCompletedQtyChangedRows(
        rightGridData,
        rightOriginalData
      );
      if (changedRows.length === 0) {
        openSnackbar("변경된 데이터가 없습니다.", "info");
        return;
      }
      setLoading(true);
      const saveData = changedRows.map((row) => ({
        item_id: row.item_id,
        order_id: row.order_id,
        proc_id: row.proc_id,
        completed_qty: row.completed_qty === null ? 0 : row.completed_qty,
        original_qty: row._original.completed_qty,
        input_qty: row.completed_qty === null ? 0 : row.completed_qty,
        group_code,
        project_code,
      }));
      createMutation.mutate(saveData, {
        onSuccess: () => {
          //   openSnackbar("저장되었습니다.", "success");
          setRightOriginalData(JSON.parse(JSON.stringify(rightGridData)));
          const updatedData = [...rightGridData].map((row) => ({
            ...row,
            style_field: false,
          }));
          setRightGridData(updatedData);
          setLoading(false);
          setLeftGridData((prevLeftGridData) => {
            const affectedItems = new Set();
            saveData.forEach((savedItem) => {
              affectedItems.add(`${savedItem.order_id}_${savedItem.item_id}`);
            });
            return prevLeftGridData.map((leftItem) => {
              const itemKey = `${leftItem.order_id}_${leftItem.item_id}`;
              if (affectedItems.has(itemKey)) {
                const matchingRightItems = rightGridData.filter(
                  (item) =>
                    item.order_id === leftItem.order_id &&
                    item.item_id === leftItem.item_id
                );
                if (matchingRightItems.length > 0) {
                  const totalQty = matchingRightItems.reduce(
                    (sum, item) => sum + (Number(item.qty) || 0),
                    0
                  );
                  const totalCompletedQty = matchingRightItems.reduce(
                    (sum, item) => sum + (Number(item.completed_qty) || 0),
                    0
                  );
                  let progressPercentage = 0;
                  if (totalQty > 0) {
                    progressPercentage = Math.round(
                      (totalCompletedQty / totalQty) * 100
                    );
                  }
                  let isError = 0;
                  const procSequences = matchingRightItems
                    .map((item) => item.proc_sequence_no)
                    .filter((seq) => seq !== undefined && seq !== null)
                    .sort((a, b) => a - b);
                  if (procSequences.length > 0) {
                    const maxSequence = Math.max(...procSequences);
                    const countDistinctSequences = new Set(procSequences).size;
                    if (countDistinctSequences < maxSequence) {
                      isError = 1;
                    }
                  }
                  if (isError === 0) {
                    const sortedBySequence = [...matchingRightItems]
                      .sort((a, b) => a.proc_sequence_no - b.proc_sequence_no)
                      .map((item) => ({
                        sequence: item.proc_sequence_no,
                        completedQty: Number(item.completed_qty) || 0,
                      }));
                    for (let i = 0; i < sortedBySequence.length - 1; i++) {
                      const currentProc = sortedBySequence[i];
                      const nextProc = sortedBySequence[i + 1];
                      if (nextProc.completedQty > currentProc.completedQty) {
                        isError = 1;
                        break;
                      }
                    }
                  }
                  return {
                    ...leftItem,
                    progress_result: progressPercentage.toString(),
                    isError,
                  };
                }
              }
              return leftItem;
            });
          });
        },
        onError: (error) => {
          openSnackbar("저장 중 오류가 발생했습니다.", "error");
          console.error("저장 오류:", error);
          setLoading(false);
        },
      });
    }, [rightGridData, rightOriginalData]);
    return (
      <CustomSaveButton
        disabled={!hasChanges}
        onClick={handleSaveClick}
        color="success"
      />
    );
  };
  const rightSideBatchUpdate = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      openSnackbar("유효한 숫자를 입력해주세요.", "warning");
      return;
    }
    if (!rightSelectedRows || rightSelectedRows.length === 0) {
      openSnackbar("업데이트할 행이 선택되지 않았습니다.", "warning");
      return;
    }
    const updatedRightGridData = [...rightGridData];
    rightSelectedRows.forEach((selectedNode) => {
      const rowIndex = updatedRightGridData.findIndex(
        (row) =>
          row.order_id === selectedNode.order_id &&
          row.proc_id === selectedNode.proc_id
      );
      if (rowIndex !== -1) {
        const row = updatedRightGridData[rowIndex];
        const newCompletedQty = numValue > row.qty ? row.qty : numValue;
        updatedRightGridData[rowIndex] = {
          ...row,
          completed_qty: newCompletedQty,
        };
        const originalValue = rightOriginalData.filter(
          (c) => c.order_id === row.order_id && c.proc_id === row.proc_id
        )[0]?.completed_qty;
        const compareToOriginalData =
          (originalValue === null || originalValue === undefined
            ? 0
            : Number(originalValue)) ===
          (newCompletedQty === null || newCompletedQty === undefined
            ? 0
            : Number(newCompletedQty));
        updatedRightGridData[rowIndex].style_field = !compareToOriginalData;
      }
    });
    setRightGridData(updatedRightGridData);
  };
  const handleBatchUpdate = async () => {
    try {
      setLoading(true);
      const filteredCompletedRows = selectedRows.filter(
        (c) => c.progress_result !== "100"
      );
      const orderIdsString = filteredCompletedRows
        .map((c) => c.order_id)
        .join(",");
      const res = await axios.get(
        // 본래는 getInsightwinPerformanceBatchUpdate 호출하여야 하지만
        // Ai 테스트를 위하여 getInsightwinPerformanceBatchUpdateForAiTest 호출함.
        APS_URL +
          `/api/performance/batchUpdate?order_id=${JSON.stringify([
            orderIdsString,
          ])}&group_code=${group_code}&project_code=${project_code}`
      );
      if (res.status === 200) {
        openSnackbar(
          res.data?.message ?? "선택한 주문 대하여 완료처리를 진행하였습니다."
        );
        setRefreshStatus((prev) => !prev);
        setRightGridData((prevRowData) => {
          return prevRowData.map((row) => {
            const isSelected = selectedRows.some(
              (selectedRow) => selectedRow.order_id === row.order_id
            );
            if (isSelected) {
              return {
                ...row,
                completed_qty: row.qty,
              };
            }
            return row;
          });
        });
        setLeftGridData((prevRowData) => {
          return prevRowData.map((row) => {
            const isSelected = selectedRows.some(
              (selectedRow) => selectedRow.order_id === row.order_id
            );
            if (isSelected) {
              return {
                ...row,
                progress_result: "100",
              };
            }
            return row;
          });
        });
        setRightOriginalData((prevRowData) => {
          return prevRowData.map((row) => {
            const isSelected = selectedRows.some(
              (selectedRow) => selectedRow.order_id === row.order_id
            );
            if (isSelected) {
              return {
                ...row,
                completed_qty: row.qty,
              };
            }
            return row;
          });
        });
        // leftGridRef.current.api.refreshCells({ force: true });
        // rightGridRef.current.api.refreshCells({ force: true });
      } else if (res.status === 500) {
        openSnackbar(res.data?.message, "error");
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
      //     toast.error("에러가 발생했습니다. 관리자에게 문의 바랍니다.");
    }
  };
  const getLeftRowStyle = useCallback(
    (params) => {
      const baseStyle = { cursor: "pointer", zIndex: 5 };
      if (!params.data) return baseStyle;
      const isRowSelected = selectedRows.some(
        (row) => row.order_id === params.data.order_id
      );
      if (isRowSelected) {
        const selectedStyle = {
          ...baseStyle,
          background: "rgb(213, 210, 247)",
          fontWeight: "bold",
        };
        if (params.data.status !== 2) {
          return {
            ...selectedStyle,
            color: "#d32f2f",
            fontStyle: "italic",
          };
        }
        return selectedStyle;
      }
      if (params.data.status !== 2) {
        return {
          ...baseStyle,
          color: "#d32f2f",
          fontStyle: "italic",
        };
      }
      if (params.data.style_field) {
        return {
          ...baseStyle,
          background: "rgba(255, 193, 7, 0.1)",
        };
      }
      return baseStyle;
    },
    [selectedRows]
  );
  const filterLeftGridData = useCallback(() => {
    if (!leftSearchText) return leftGridData;
    const searchTermLower = leftSearchText.toLowerCase();
    return leftGridData.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(searchTermLower)
      );
    });
  }, [leftGridData, leftSearchText]);
  const filterRightGridData = useCallback(() => {
    if (!rightSearchText) return rightGridData;
    const searchTermLower = rightSearchText.toLowerCase();
    return rightGridData.filter((row) => {
      return Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(searchTermLower)
      );
    });
  }, [rightGridData, rightSearchText]);
  const filteredLeftData = useMemo(
    () => filterLeftGridData(),
    [filterLeftGridData]
  );
  const filteredRightData = useMemo(
    () => filterRightGridData(),
    [filterRightGridData]
  );
  return (
    <>
      <GridContainer>
        <div className="inner-col">
          <ButtonBox>
            <TextField
              placeholder="검색어를 입력하세요"
              variant="outlined"
              sx={{
                width: "25%",
                marginBottom: "0px",
              }}
              size="small"
              value={leftSearchText}
              onChange={onLeftFilterChanged}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <CustomMuiButton
              variant="contained"
              color="primary"
              disabled={!selectedRows.length}
              onClick={handleBatchUpdate}
              size="medium"
              startIcon={<LibraryAddCheckIcon />}
            >
              완료 및 저장
            </CustomMuiButton>
            {/* <CustomButton
              variant="contained"
              color="primary"
              disabled={!selectedRows.length}
              onClick={handleBatchUpdate}
              size="medium"
            >
              
            </CustomButton> */}
          </ButtonBox>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AgGridReact
              rowData={filteredLeftData}
              ref={leftGridRef}
              columnDefs={leftColDef}
              rowSelection={rowLeftSelection}
              getRowId={(params) => String(params.data.order_id)}
              onRowClicked={onLeftGridClick}
              localeText={customGermanLocale}
              theme={themeQuartz}
              rowStyle={{
                fontWeight: "500",
                fontsize: "14px",
              }}
              defaultColDef={defaultColDef}
              rowHeight={48}
              suppressScrollOnNewData={true}
              getRowStyle={getLeftRowStyle}
            />
          </div>
        </div>
        <div className="inner-col">
          <ButtonBox>
            <TextField
              placeholder="검색어를 입력하세요"
              variant="outlined"
              sx={{
                width: "25%",
                marginBottom: "0px",
              }}
              size="small"
              value={rightSearchText}
              onChange={onRightFilterChanged}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <BulkSelectionButton
                disabled={!rightSelectedRows.length}
                onSave={rightSideBatchUpdate}
              />
              <CustomMuiButton
                variant="contained"
                color="primary"
                disabled={!rightSelectedRows.length}
                onClick={handleRightComplete}
                size="medium"
                startIcon={<LibraryAddCheckIcon />}
              >
                완료처리
              </CustomMuiButton>
              <SaveButton />
            </div>
          </ButtonBox>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <AgGridReact
              theme={themeQuartz}
              rowData={filteredRightData}
              ref={rightGridRef}
              columnDefs={rightColDef}
              rowStyle={{
                zIndex: 5,
                fontWeight: "500",
                fontsize: "14px",
              }}
              onSelectionChanged={onRightSelectionChanged}
              onRowClicked={(e) => console.log(e)}
              localeText={customGermanLocale}
              rowSelection={rowRightSelection}
              overlayNoRowsTemplate={noRowsOverlayComponent}
              onCellEditingStopped={onChangeRightQty}
              getRowStyle={getRightRowStyle}
              defaultColDef={defaultColDef}
              rowHeight={48}
              stopEditingWhenCellsLoseFocus={true}
              suppressMovableColumns={true}
              suppressScrollOnNewData={true}
            />
          </div>
        </div>
      </GridContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {loading && <Loading />}
    </>
  );
}

export default PerformanceGrid;
